/* eslint-disable no-alert */
import { Button, Form, Modal, Select, Spin, Steps } from "antd";
import { useCallback, useEffect, useState } from "react";
import { IoQrCodeOutline } from "react-icons/io5";
import QRCode from "qrcode";
import JsPDF from "jspdf";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";

const SELECT_PROPS = {
    mode: "multiple",
    style: { width: "100%" },
    maxTagCount: "responsive",
};

const OFFSCREEN_PDF_RENDER_STYLES = {
    position: "fixed",
    left: "-10000px",
    top: "0",
    pointerEvents: "none",
    zIndex: "-1",
};

// const SELECT_ALL_OPTION = { value: "select-all", label: "All Cars" };

const generateQRCodeContainer = async (car, t) => {
    try {
        // Create QR container
        const qrContainer = document.createElement("div");
        qrContainer.style.textAlign = "center";
        qrContainer.style.border = "2px solid #000";
        qrContainer.style.padding = "15px";
        qrContainer.style.borderRadius = "8px";
        qrContainer.style.backgroundColor = "#fff";
        qrContainer.style.pageBreakInside = "avoid";

        // Create QR code canvas (1.8 inches)
        const canvas = document.createElement("canvas");
        await QRCode.toCanvas(canvas, car.VIN, {
            width: 180, // 1.8 inches at 96 DPI
            margin: 0,
            color: { dark: "#000000", light: "#FFFFFF" },
        });
        canvas.style.display = "block";
        canvas.style.margin = "0 auto";

        // Create car number element
        const carNumberElement = document.createElement("div");
        carNumberElement.textContent = car.carNumber || "N/A";
        carNumberElement.style.marginTop = "8px";
        carNumberElement.style.fontSize = "20px";
        carNumberElement.style.fontWeight = "bold";
        carNumberElement.style.color = "#000";
        carNumberElement.style.wordBreak = "break-word";

        // Create car name element (fixed 2-line height)
        const carNameElement = document.createElement("div");
        carNameElement.textContent = car.carName || "N/A";
        carNameElement.style.fontSize = "14px";
        carNameElement.style.color = "#666";
        carNameElement.style.marginTop = "4px";
        carNameElement.style.wordBreak = "break-word";
        carNameElement.style.lineHeight = "1.2";
        carNameElement.style.height = "40px"; // Fixed height for 2 lines
        carNameElement.style.overflow = "hidden";
        carNameElement.style.display = "-webkit-box";
        carNameElement.style.webkitLineClamp = "2";
        carNameElement.style.webkitBoxOrient = "vertical";
        carNameElement.style.textOverflow = "ellipsis";

        // Append elements to container
        qrContainer.appendChild(canvas);
        qrContainer.appendChild(carNumberElement);
        qrContainer.appendChild(carNameElement);

        return qrContainer;
    } catch (_error) {
        // Create error container
        const errorContainer = document.createElement("div");
        errorContainer.style.textAlign = "center";
        errorContainer.style.border = "2px solid #ff0000";
        errorContainer.style.padding = "15px";
        errorContainer.style.borderRadius = "8px";
        errorContainer.style.backgroundColor = "#ffe6e6";
        errorContainer.style.pageBreakInside = "avoid";

        const errorText = document.createElement("div");
        errorText.textContent = t("generateQR.errorGeneratingQR", { carNumber: car.carNumber });
        errorText.style.color = "#ff0000";
        errorText.style.fontWeight = "bold";

        errorContainer.appendChild(errorText);
        return errorContainer;
    }
};

function GenerateQR() {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [allCars, setAllCars] = useState([]);
    const [carSelectOptions, setCarSelectOptions] = useState([]);
    const [selectedCarIds, setSelectedCarIds] = useState([]);
    const [selectedDisplayValues, setSelectedDisplayValues] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedCarData, setSelectedCarData] = useState([]);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState("");
    const toast = useToast();
    const { t } = useTranslation("cars");
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

    useEffect(() => {
        const fetchCarData = async () => {
            try {
                const response = await apiClient.get("/api/car/list?limit=100000");
                const carsData = response.data.data.result;
                setAllCars(carsData);

                const selectOptions = carsData.map((car) => ({
                    value: car.id,
                    label: `${car.carNumber} | ${car.carName}`,
                }));

                const optionsWithSelectAll = [
                    { value: "select-all", label: t("generateQR.allCars") },
                    ...selectOptions,
                ];
                setCarSelectOptions(optionsWithSelectAll);
            } catch (_error) {
                toast.error({ message: t("generateQR.errorFetchingCarData") });
            }
        };

        fetchCarData();
    }, [toast, t]);

    const handleCarSelection = (selectedValues) => {
        const isSelectAllIncluded = selectedValues.includes("select-all");
        const isEmptySelection = selectedValues.length === 0;

        if (isSelectAllIncluded) {
            const allCarIds = carSelectOptions
                .filter((option) => option.value !== "select-all")
                .map((option) => option.value);
            setSelectedCarIds(allCarIds);
            setSelectedDisplayValues(["select-all"]);
        } else if (isEmptySelection) {
            setSelectedCarIds([]);
            setSelectedDisplayValues([]);
        } else {
            const filteredValues = selectedValues.filter((value) => value !== "select-all");
            setSelectedCarIds(filteredValues);
            setSelectedDisplayValues(filteredValues);
        }
    };

    const openModal = () => setIsModalVisible(true);

    const cleanupPdfPreview = () => {
        if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl("");
        }
    };

    const resetModalState = () => {
        setCurrentStep(0);
        setSelectedCarIds([]);
        setSelectedDisplayValues([]);
        setSelectedCarData([]);
        setIsGeneratingPreview(false);
        cleanupPdfPreview();
    };

    const closeModal = () => {
        setIsModalVisible(false);
        resetModalState();
    };

    const createQRCodesHTML = useCallback(async () => {
        // Create main container
        const containerElement = document.createElement("div");
        containerElement.style.backgroundColor = "#ffffff";
        containerElement.style.fontFamily = "Arial, sans-serif";

        // Define page layout: 3 columns × 3 rows = 9 QR codes per page
        const COLUMNS_PER_PAGE = 3;
        const ROWS_PER_PAGE = 3;
        const QR_CODES_PER_PAGE = COLUMNS_PER_PAGE * ROWS_PER_PAGE;

        // Split cars into chunks for each page
        const carChunks = [];
        for (let i = 0; i < selectedCarData.length; i += QR_CODES_PER_PAGE) {
            carChunks.push(selectedCarData.slice(i, i + QR_CODES_PER_PAGE));
        }

        // Generate pages
        for (let pageIndex = 0; pageIndex < carChunks.length; pageIndex += 1) {
            const carChunk = carChunks[pageIndex];

            // Create page container
            const pageContainer = document.createElement("div");
            pageContainer.className = "qr-page-container";
            pageContainer.style.minHeight = "1123px"; // A4 height in pixels
            pageContainer.style.width = "794px"; // A4 width in pixels
            pageContainer.style.padding = "40px";
            pageContainer.style.boxSizing = "border-box";
            pageContainer.style.backgroundColor = "#ffffff";
            pageContainer.style.pageBreakAfter = pageIndex < carChunks.length - 1 ? "always" : "auto";

            // Create grid layout for this page
            const qrCodesGrid = document.createElement("div");
            qrCodesGrid.style.display = "grid";
            qrCodesGrid.style.gridTemplateColumns = `repeat(${COLUMNS_PER_PAGE}, 1fr)`;
            qrCodesGrid.style.gridTemplateRows = `repeat(${ROWS_PER_PAGE}, 1fr)`;
            qrCodesGrid.style.gap = "30px";
            qrCodesGrid.style.justifyItems = "center";
            qrCodesGrid.style.alignItems = "center";
            qrCodesGrid.style.height = "100%";
            qrCodesGrid.style.width = "100%";

            // Generate QR codes for each car in this chunk
            for (let i = 0; i < carChunk.length; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                const qrContainer = await generateQRCodeContainer(carChunk[i], t);
                qrCodesGrid.appendChild(qrContainer);
            }

            pageContainer.appendChild(qrCodesGrid);
            containerElement.appendChild(pageContainer);
        }

        return containerElement;
    }, [selectedCarData, t]);

    const createPDFDocument = useCallback(async () => {
        const htmlElement = await createQRCodesHTML();
        htmlElement.setAttribute("aria-hidden", "true");
        Object.assign(htmlElement.style, OFFSCREEN_PDF_RENDER_STYLES);
        document.body.appendChild(htmlElement);

        try {
            const pageContainers = htmlElement.querySelectorAll(".qr-page-container");
            const pdf = new JsPDF("p", "mm", "a4");

            const canvasPromises = Array.from(pageContainers).map((pageContainer) =>
                html2canvas(pageContainer, {
                    scale: 1.5,
                    backgroundColor: "#ffffff",
                    logging: false,
                    useCORS: true,
                    allowTaint: true,
                    foreignObjectRendering: false,
                    removeContainer: false,
                }),
            );

            const canvases = await Promise.all(canvasPromises);

            for (let i = 0; i < canvases.length; i += 1) {
                const canvas = canvases[i];

                if (i > 0) {
                    pdf.addPage();
                }

                const imgData = canvas.toDataURL("image/png");
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = pdf.internal.pageSize.getHeight();

                const canvasRatio = canvas.width / canvas.height;
                const pdfRatio = pdfWidth / pdfHeight;

                let imgWidth = pdfWidth;
                let imgHeight = pdfHeight;
                let xOffset = 0;
                let yOffset = 0;

                if (canvasRatio > pdfRatio) {
                    imgHeight = pdfWidth / canvasRatio;
                    yOffset = (pdfHeight - imgHeight) / 2;
                } else {
                    imgWidth = pdfHeight * canvasRatio;
                    xOffset = (pdfWidth - imgWidth) / 2;
                }

                pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);
            }

            return pdf;
        } finally {
            document.body.removeChild(htmlElement);
        }
    }, [createQRCodesHTML]);

    const downloadPDF = async () => {
        try {
            const pdf = await createPDFDocument();
            const fileName = t("generateQR.qrCodeFileName", {
                date: new Date().toISOString().split("T")[0],
            });
            pdf.save(fileName);
        } catch (_error) {
            toast.error({ message: t("generateQR.errorGeneratingPDF") });
        }
    };

    const generateQRCodes = () => {
        if (selectedCarIds.length === 0) {
            toast.warning({ message: t("generateQR.selectAtLeastOneCar") });
            return;
        }

        const selectedCars = allCars.filter((car) => selectedCarIds.includes(car.id));

        if (selectedCars.length === 0) {
            toast.error({ message: t("generateQR.noValidCarsFound") });
            return;
        }

        // Validate that selected cars have VIN data
        const carsWithoutVIN = selectedCars.filter((car) => !car.VIN || car.VIN.trim() === "");
        if (carsWithoutVIN.length > 0) {
            toast.warning({ message: t("generateQR.carsWithoutVIN") });
            return;
        }
        cleanupPdfPreview();
        setSelectedCarData(selectedCars);
        setCurrentStep(1);
    };

    const goBackToSelection = () => {
        cleanupPdfPreview();
        setCurrentStep(0);
    };

    useEffect(() => {
        if (currentStep !== 1 || selectedCarData.length === 0) {
            return undefined;
        }

        let cancelled = false;

        const generatePDFPreview = async () => {
            setIsGeneratingPreview(true);
            try {
                const pdf = await createPDFDocument();
                if (cancelled) {
                    return;
                }
                const pdfBlob = pdf.output("blob");
                const previewUrl = URL.createObjectURL(pdfBlob);
                setPdfPreviewUrl(previewUrl);
            } catch {
                if (!cancelled) {
                    toast.error({ message: t("generateQR.errorGeneratingPDF") });
                }
            } finally {
                if (!cancelled) {
                    setIsGeneratingPreview(false);
                }
            }
        };

        generatePDFPreview();

        return () => {
            cancelled = true;
        };
    }, [createPDFDocument, currentStep, toast, selectedCarData, t]);

    useEffect(() => {
        if (currentStep !== 1 || pdfPreviewUrl) {
            return undefined;
        }
        const previousOverflowX = document.body.style.overflowX;
        document.body.style.overflowX = "hidden";
        return () => {
            document.body.style.overflowX = previousOverflowX;
        };
    }, [currentStep, pdfPreviewUrl]);

    return (
        <div className="min-w-0">
            <Button type="primary" shape="round" icon={<IoQrCodeOutline />} onClick={openModal}>
                {t("generateQR.generateButton")}
            </Button>

            <Modal
                title={t("generateQR.title")}
                open={isModalVisible}
                onCancel={closeModal}
                centered
                maskClosable={false}
                destroyOnHidden
                cancelButtonProps={{ shape: "round" }}
                rootClassName="generate-qr-modal"
                classNames={{ body: "min-w-0", footer: "!block w-full" }}
                styles={{
                    body: {
                        maxHeight: "min(82vh, 720px)",
                        overflowY: "auto",
                        paddingTop: 8,
                        paddingBottom: 8,
                    },
                }}
                width={{
                    xs: "calc(100vw - 2rem)",
                    sm: "80%",
                    md: "70%",
                    lg: "70%",
                    xl: "60%",
                    xxl: "60%",
                }}
                footer={
                    <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                        {currentStep === 0 ? (
                            <>
                                <Button
                                    type="primary"
                                    shape="round"
                                    icon={<IoQrCodeOutline />}
                                    onClick={generateQRCodes}
                                    className="!m-0 w-full sm:w-auto">
                                    {t("generateQR.generateButton")}
                                </Button>
                                <Button shape="round" onClick={closeModal} className="!m-0 w-full sm:w-auto">
                                    {t("generateQR.cancel")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="primary"
                                    shape="round"
                                    onClick={downloadPDF}
                                    disabled={isGeneratingPreview || !pdfPreviewUrl}
                                    className="!m-0 w-full sm:order-2 sm:w-auto">
                                    {t("generateQR.downloadPDF")}
                                </Button>
                                <Button
                                    shape="round"
                                    onClick={goBackToSelection}
                                    disabled={isGeneratingPreview}
                                    className="!m-0 w-full sm:order-1 sm:w-auto">
                                    {t("generateQR.back")}
                                </Button>
                                <Button shape="round" onClick={closeModal} className="!m-0 w-full sm:order-3 sm:w-auto">
                                    {t("generateQR.cancel")}
                                </Button>
                            </>
                        )}
                    </div>
                }>
                <div
                    className="mb-2 sm:mb-4 [&_.ant-steps-item-container]:!flex [&_.ant-steps-item-title]:!text-xs [&_.ant-steps-item-title]:!leading-tight sm:[&_.ant-steps-item-title]:!text-sm [&_.ant-steps]:!mb-0">
                    <Steps
                        size="small"
                        direction="horizontal"
                        responsive={false}
                        current={currentStep}
                        items={[{ title: t("generateQR.carSelection") }, { title: t("generateQR.downloadPrintQR") }]}
                    />
                </div>

                {currentStep === 0 ? (
                    <div>
                        <Form.Item label={t("generateQR.selectCars")} layout="vertical">
                            <Select
                                {...SELECT_PROPS}
                                options={carSelectOptions}
                                placeholder={t("generateQR.selectCarsPlaceholder")}
                                value={selectedDisplayValues}
                                onChange={handleCarSelection}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </div>
                ) : (
                    <div className="min-w-0">
                        <div
                            className="mx-auto mt-2 w-full max-w-full overflow-hidden rounded-lg bg-white shadow-lg min-h-[14rem] h-[min(58vh,560px)] sm:mt-6 sm:h-[600px]">
                            {pdfPreviewUrl && !isGeneratingPreview ? (
                                <object
                                    data={pdfPreviewUrl}
                                    type="application/pdf"
                                    width="100%"
                                    height="100%"
                                    className="w-full h-full">
                                    <div className="flex items-center justify-center h-full bg-gray-50">
                                        <div className="text-center">
                                            <p className="text-gray-600 mb-4">
                                                {t("generateQR.pdfPreviewNotSupported")}
                                            </p>
                                            <Button type="primary" onClick={downloadPDF}>
                                                {t("generateQR.downloadPDFToView")}
                                            </Button>
                                        </div>
                                    </div>
                                </object>
                            ) : (
                                <div className="flex items-center justify-center h-full bg-gray-50">
                                    <div className="text-center">
                                        <Spin />
                                        <p className="text-gray-600">{t("generateQR.generatingPDFPreview")}</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-3 text-center text-xs text-gray-600 sm:mt-5 sm:text-sm">
                            {t("generateQR.qrCodesReady", { count: selectedCarData.length })}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}

export default GenerateQR;
